// src/sections/admin/UsersSection.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ResourceTable from '../../components/admin/ResourceTable';
import UserForm from '../../components/admin/UserForm';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UsersSection = ({ user, users = [], services, counters = [], kiosks = [], setMsg, fetchAll }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const adminTenantId = user?.tenant_id != null ? parseInt(user.tenant_id, 10) : null; 

  // --- Diagnostic Logs: Keep these for now ---
  useEffect(() => {
    
    const currentlyFilteredUsers = users.filter(u => String(u.tenant_id) === String(adminTenantId));
    console.log("UsersSection: Filtered users array (after applying tenant_id filter):", currentlyFilteredUsers);
    console.log("UsersSection: Filtered users length:", currentlyFilteredUsers.length);
    
    if (currentlyFilteredUsers.length === 0) {
      console.log("UsersSection: No users found after tenant_id filtering.");
    } else {
      console.log("UsersSection: Users found after tenant_id filtering!");
    }
    console.log("-----------------------------------------");
  }, [user, users, adminTenantId]);

  const userColumns = [
    { key: 'id', label: 'ID' },
    { key: 'username', label: 'Username' },
    { key: 'role', label: 'Role' },
    { 
      key: 'counter_id', 
      label: 'Counter', 
      render: (counterId) => {
        if (counterId == null) return 'N/A';
        const match = counters.find(c => String(c.id) === String(counterId));
        return match ? match.number : `ID:${counterId}`;
      }
    }, 
    { 
      key: 'kiosk_id', 
      label: 'Kiosk', 
      render: (kioskId) => (kioskId != null ? kiosks.find(k => String(k.id) === String(kioskId))?.name || String(kioskId) : 'N/A')
    }, 
    { key: 'tenant_id', label: 'Tenant ID' }
  ];

  const handleAddUser = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEditUser = (userData) => {
    setEditingUser(userData);
    setShowForm(true);
  };

  const handleDeleteUser = async (userData) => {
    if (window.confirm(`Are you sure you want to delete user ${userData.username}?`)) {
      try {
        await axios.delete(`${BASE_URL}/api/users/${userData.id}`);
        setMsg({ type: 'success', text: `User ${userData.username} deleted successfully!` });
        fetchAll();
      } catch (error) {
        setMsg({ type: 'error', text: `Failed to delete user: ${error.message}` });
        console.error("Error deleting user:", error);
      }
    }
  };

  // --- CRITICAL FIX: Robust handleSubmitUserForm ---
  const handleSubmitUserForm = async (formData, isEditMode) => {
    try {
      // Create a copy to modify and send
      const dataToSubmit = { ...formData }; 

      // Convert empty strings to null for optional number fields (counter_id, kiosk_id)
      // and parse them to integers if they have a value.
      if (dataToSubmit.counter_id === "") {
        dataToSubmit.counter_id = null;
      } else if (dataToSubmit.counter_id !== null && typeof dataToSubmit.counter_id === 'string') {
        // Ensure it's parsed as an integer if it's a non-empty string
        dataToSubmit.counter_id = parseInt(dataToSubmit.counter_id, 10);
        if (isNaN(dataToSubmit.counter_id)) { // Handle cases where parsing might fail (e.g., non-numeric string)
          dataToSubmit.counter_id = null;
        }
      }

      if (dataToSubmit.kiosk_id === "") {
        dataToSubmit.kiosk_id = null;
      } else if (dataToSubmit.kiosk_id !== null && typeof dataToSubmit.kiosk_id === 'string') {
        // Ensure it's parsed as an integer
        dataToSubmit.kiosk_id = parseInt(dataToSubmit.kiosk_id, 10);
        if (isNaN(dataToSubmit.kiosk_id)) {
          dataToSubmit.kiosk_id = null;
        }
      }

      // Ensure tenant_id is always correctly set for new users (from admin's tenant)
      if (!isEditMode) {
        dataToSubmit.tenant_id = adminTenantId; 
      }

      if (isEditMode) {
        // PATCH request for existing user
        await axios.patch(`${BASE_URL}/api/users/${formData.id}`, dataToSubmit); // Use dataToSubmit
        setMsg({ type: 'success', text: `User ${formData.username} updated successfully!` });
      } else {
        // POST request for new user
        await axios.post(`${BASE_URL}/api/users`, dataToSubmit); // Use dataToSubmit
        setMsg({ type: 'success', text: `User ${formData.username} added successfully!` });
      }
      setShowForm(false);    // Hide the form
      setEditingUser(null);  // Clear editing state
      fetchAll();            // Re-fetch all data to update the table
    } catch (error) {
      setMsg({ type: 'error', text: `Failed to ${isEditMode ? 'update' : 'add'} user: ${error.message}` });
      console.error("Error submitting user form:", error);
    }
  };

  // Patch: Show all users for vendor_admin, only own-tenant users for admin
  let filteredUsers = users;
  if (user?.role === 'admin') {
    filteredUsers = users.filter(u => String(u.tenant_id) === String(adminTenantId));
  } else if (user?.role === 'vendor_admin' || String(user?.tenant_id) === '999') {
    // Show all users
    filteredUsers = users;
  }

  return (
    <div>
      <h2 className="admin-section-header">User Management</h2>
      {!showForm ? (
        <>
          <button onClick={handleAddUser} className="admin-button primary" style={{ marginBottom: '1.5rem' }}>Add New User</button>
          <ResourceTable 
            data={filteredUsers} 
            columns={userColumns} 
            onEdit={handleEditUser} 
            onDelete={handleDeleteUser} 
            title="Users"
          />
        </>
      ) : (
        <UserForm 
          user={user} 
          initialData={editingUser || {}} 
          services={services} 
          counters={counters} 
          kiosks={kiosks} 
          onSubmit={handleSubmitUserForm} 
          onCancel={() => setShowForm(false)} 
        />
      )}
    </div>
  );
};

export default UsersSection;